import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import LivretAccessBanner from '../../components/livrets/LivretAccessBanner';
import {
  ArrowLeft,
  ClipboardCheck,
  AlertTriangle,
  Shirt,
  Timer,
  AlertOctagon,
  RotateCcw,
  Printer,
  Target,
  Sparkles,
  Star,
  Presentation,
  ArrowRight,
  Trophy,
} from 'lucide-react';
import { ScoreRing } from '../../components/ScoreRing';
import { CelebrateBurst } from '../../components/CelebrateBurst';
import { PlateauInteractive } from '../../components/plateau/PlateauInteractive';
import RelatedLinks from '../../components/RelatedLinks';
import useSeo from '../../hooks/useSeo';
import { buildBreadcrumb, combineJsonLd } from '../../seo/breadcrumbs';
import { buildHowToSchema, buildFaqSchema, buildLearningResourceSchema } from '../../seo/schemas';

const STORAGE_KEY = 'code2roues:epreuves:plateau:v1';

const OBJECTIFS = [
  "Maîtrise de la moto sans l'aide du moteur",
  'Maîtrise à allure réduite sans passager',
  "Maîtrise du freinage à allure élevée (freinage d'urgence)",
  'Maîtrise à allure réduite avec passager',
  "Maîtrise du slalom et de la manœuvre d'évitement à allure élevée",
];

const EQUIPEMENT = [
  'Casque homologué',
  'Gants certifiés CE',
  'Blouson avec protections',
  'Pantalon ou combinaison adaptée',
  'Chaussures montantes',
];

const RAPPELS = [
  { icon: Timer, text: 'Parcours chronométré et noté' },
  { icon: Target, text: '1 essai pour la manœuvre 1' },
  { icon: Target, text: '2 essais pour les manœuvres 2 à 6' },
  { icon: AlertOctagon, text: 'Pieds au sol autorisés : 3 fois à partir de la manœuvre 2' },
  { icon: AlertOctagon, text: 'Chaque faute supplémentaire = 1 erreur' },
  { icon: AlertOctagon, text: 'Respecter les cônes et zones définies' },
];

// Palette d'accents par manœuvre (alignée avec home/Plateau interactif)
const MANOEUVRES = [
  {
    id: 1,
    title: "Sans l'aide du moteur",
    accent: '#7dd3fc',
    steps: [
      'Débéquiller en A',
      'Déplacer la moto en avant en marchant à côté',
      "S'arrêter en B",
      'Reculer en marchant à côté',
      'Rebéquiller en A',
    ],
  },
  {
    id: 2,
    title: 'Allure réduite sans passager',
    accent: '#86efac',
    steps: [
      'Démarrer',
      'Parcours chronométré entre A et B au passage de la roue arrière. Note A si chrono ≥ 16 s.',
      "Franchir le couloir d'évitement",
      'Arrêt de précision pied à terre',
    ],
  },
  {
    id: 3,
    title: "Freinage d'urgence",
    accent: '#fde68a',
    steps: [
      'Boucle à vitesse lente',
      'Aller en bout de piste et faire demi-tour',
      "Accélérer à au moins 50 km/h (radar en C6) puis effectuer un freinage d'urgence",
      'Arrêt avant : C5 (temps sec) ou C4 (route mouillée)',
    ],
  },
  {
    id: 4,
    title: 'Allure réduite avec passager',
    accent: '#3b82f6',
    steps: [
      'Prendre un passager',
      'Effectuer un parcours en boucle avec passager',
      "S'arrêter avec précision et déposer le passager",
    ],
  },
  {
    id: 5,
    title: 'Slalom',
    accent: '#f97316',
    steps: [
      'Slalom à au moins 40 km/h (radar C7)',
      'Effectuer un demi-tour',
      'Accélérer à au moins 50 km/h (radar C6)',
    ],
  },
  {
    id: 6,
    title: 'Évitement',
    accent: '#dc2626',
    steps: [
      "Effectuer un évitement à 50 km/h",
      'Freinage de précision entre les plots de couleur',
    ],
  },
];

const PLATEAU_HERO_IMAGE =
  'https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=1800&q=85&auto=format&fit=crop';

const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    console.warn('[Plateau] localStorage read failed:', e);
  }
  return { manoeuvres: {}, equipement: {} };
};

export default function Plateau() {
  const navigate = useNavigate();
  const [state, setState] = useState(loadState);

  // SEO : pour cibler "plateau moto", "6 manoeuvres plateau", "fiche plateau A2"
  useSeo({
    title: 'Plateau Moto · 6 manœuvres officielles · Permis A1 / A2',
    description: "Fiche plateau moto complète 2026 : 6 manœuvres officielles (allure lente, freinage, slalom, évitement, passager). Critères de notation, équipement obligatoire, schémas du roadbook officiel.",
    keywords: 'plateau moto, 6 manoeuvres plateau, fiche plateau A2, plateau moto pdf, allure lente moto, freinage urgence moto, slalom moto, evitement moto, plateau permis A2, plateau permis A1, plateau moto 2026, notation plateau ABC',
    path: '/epreuves/plateau',
    preloadImage: PLATEAU_HERO_IMAGE,
    jsonLd: combineJsonLd(
      buildBreadcrumb([
        ['Plateau & Circulation', '/epreuves'],
        ['Plateau · 6 manœuvres', '/epreuves/plateau'],
      ]),
      buildLearningResourceSchema({
        name: 'Fiche Plateau Moto · 6 manœuvres officielles 2026',
        description: "Roadbook officiel des 6 manœuvres de l'épreuve plateau du permis A1/A2 : allure lente, freinage, slalom, évitement, passager.",
        path: '/epreuves/plateau',
        learningResourceType: 'Tutorial',
        about: 'Plateau moto, permis A1, permis A2',
      }),
      buildHowToSchema({
        name: 'Comment réussir l\'épreuve plateau moto (permis A1/A2)',
        description: '6 manœuvres officielles à maîtriser pour valider le plateau du permis A1 ou A2 : allure lente, freinage d\'urgence, slalom, évitement, transport passager.',
        totalTime: 'PT15M',
        steps: [
          { name: 'Manœuvre 1 · Maîtrise sans le moteur', text: 'Pousser la moto à pied, descendre de selle puis béquiller. 1 seul essai autorisé. Évalue ton équilibre et ta connaissance de la machine.' },
          { name: 'Manœuvre 2 · Allure lente', text: 'Parcourir un tracé à très basse vitesse sans poser le pied. Vise un temps minimum officiel pour la zone définie. Regard loin, point de patinage maîtrisé.' },
          { name: 'Manœuvre 3 · Freinage d\'urgence', text: 'Freiner fort en ligne droite à allure élevée (vitesse cible officielle). Frein avant + arrière dosés, sans blocage. Distance d\'arrêt mesurée.' },
          { name: 'Manœuvre 4 · Allure réduite avec passager', text: 'Refaire un parcours d\'équilibre à basse vitesse avec passager (mannequin ou personne). Évalue ta stabilité avec charge supplémentaire.' },
          { name: 'Manœuvre 5 · Slalom + évitement à allure élevée', text: 'Enchaîner le slalom puis l\'évitement à vitesse imposée. Trajectoires précises, regard anticipé sur la sortie.' },
          { name: 'Manœuvre 6 · Récapitulatif final', text: 'Démontrer l\'enchaînement complet sous contrôle de l\'examinateur. Notation A (favorable), B (à reprendre), C (défavorable).' },
        ],
      }),
      buildFaqSchema([
        {
          q: 'Combien de manœuvres y a-t-il à l\'épreuve plateau du permis moto ?',
          a: '6 manœuvres officielles : 1) maîtrise sans le moteur, 2) allure lente, 3) freinage d\'urgence, 4) allure lente avec passager, 5) slalom + évitement à allure élevée, 6) récapitulatif final.',
        },
        {
          q: 'Quelle est la notation du plateau moto (A, B, C) ?',
          a: 'A = favorable (réussite), B = à reprendre (défaut sans danger), C = défavorable (échec). Le candidat doit obtenir A à toutes les manœuvres pour valider le plateau.',
        },
        {
          q: 'Combien d\'essais sont autorisés au plateau moto ?',
          a: '1 seul essai pour la manœuvre 1 (maîtrise sans le moteur). 2 essais pour les manœuvres 2 à 6. Pieds au sol autorisés 3 fois maximum à partir de la manœuvre 2.',
        },
        {
          q: 'Quel équipement faut-il pour passer le plateau moto ?',
          a: 'Casque homologué, gants certifiés CE, blouson avec protections, pantalon ou combinaison adaptée, chaussures montantes. Sans cet équipement complet, le candidat ne peut pas se présenter.',
        },
        {
          q: 'Quelle vitesse pour le freinage d\'urgence au plateau ?',
          a: 'Vitesse cible officielle : 50 km/h pour le permis A1/B1, 60 km/h pour le permis A2 (en zone d\'élan). La distance d\'arrêt est mesurée et doit respecter le seuil défini par l\'examinateur.',
        },
      ]),
    ),
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn('[Plateau] localStorage write failed:', e);
    }
  }, [state]);

  const toggleManoeuvre = useCallback((mId, sIdx) => {
    setState((s) => {
      const cur = { ...(s.manoeuvres || {}) };
      const setForM = new Set(cur[mId] || []);
      if (setForM.has(sIdx)) setForM.delete(sIdx);
      else setForM.add(sIdx);
      cur[mId] = Array.from(setForM);
      return { ...s, manoeuvres: cur };
    });
  }, []);

  const toggleEquipement = useCallback((idx) => {
    setState((s) => {
      const cur = { ...(s.equipement || {}) };
      cur[idx] = !cur[idx];
      return { ...s, equipement: cur };
    });
  }, []);

  const reset = () => {
    if (window.confirm('Réinitialiser tes acquis sur cette fiche ?'))
      setState({ manoeuvres: {}, equipement: {} });
  };

  const totalSteps = MANOEUVRES.reduce((a, m) => a + m.steps.length, 0);
  const doneSteps = MANOEUVRES.reduce(
    (a, m) => a + (state.manoeuvres?.[m.id] || []).length,
    0,
  );
  const pct = totalSteps ? Math.round((doneSteps / totalSteps) * 100) : 0;

  // Track sections complete pour déclencher le burst une seule fois par complétion
  const completedSet = useMemo(() => {
    const set = {};
    MANOEUVRES.forEach((m) => {
      const done = (state.manoeuvres?.[m.id] || []).length;
      set[m.id] = done === m.steps.length && done > 0;
    });
    return set;
  }, [state.manoeuvres]);

  const [burstTriggers, setBurstTriggers] = useState({});
  const prevCompleted = React.useRef({});
  useEffect(() => {
    Object.keys(completedSet).forEach((id) => {
      if (completedSet[id] && !prevCompleted.current[id]) {
        setBurstTriggers((t) => ({ ...t, [id]: (t[id] || 0) + 1 }));
      }
    });
    prevCompleted.current = { ...completedSet };
  }, [completedSet]);

  return (
    <div className="space-y-8 sm:space-y-12 print:space-y-3" data-testid="plateau-page">
      <Button
        onClick={() => navigate('/epreuves')}
        variant="ghost"
        size="sm"
        className="-ml-2 print:hidden"
        data-testid="plateau-back"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour aux épreuves
      </Button>

      {/* Accès direct au livret Plateau interactif — visible dès le haut de la page */}
      <LivretAccessBanner
        livretTo="/livret-plateau"
        livretName="Livret Plateau A1/A2 — 7 chapitres à cocher"
        desc="Coche tes acquis pendant ou après chaque séance de plateau. Sauvegarde locale, pas besoin de compte."
      />

      {/* ====================================================================
          HERO CINÉMA — image moto + kicker rouge + titre Playfair + ring
          ==================================================================== */}
      <section
        className="relative overflow-hidden rounded-2xl bg-card hero-content-fade"
        data-testid="plateau-hero"
        style={{ boxShadow: '0 12px 32px -16px rgba(0,0,0,0.5)' }}
      >
        {/* Image cinéma full bleed */}
        <div className="relative w-full overflow-hidden aspect-[5/3] sm:aspect-[16/8] lg:aspect-[21/9] max-h-[440px]">
          <img
            src={PLATEAU_HERO_IMAGE}
            alt="Moto sur piste d'examen plateau"
            className="absolute inset-0 w-full h-full object-cover hero-image-cinematic"
            loading="eager"
            fetchpriority="high"
            style={{ filter: 'saturate(0.92) contrast(1.04)' }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, rgba(15,12,8,0.05) 0%, rgba(15,12,8,0.30) 55%, rgba(15,12,8,0.95) 100%)',
            }}
          />
          {/* Tag officiel top-left */}
          <span className="absolute top-4 left-4 sm:top-6 sm:left-6 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--primary))]/95 text-white text-[10px] font-black uppercase tracking-[0.14em] backdrop-blur-md shadow-lg">
            <Star className="w-3 h-3 fill-current" />
            Examen officiel · 2026
          </span>
        </div>

        {/* Bloc texte sous l'image */}
        <div className="relative px-6 py-7 sm:px-10 sm:py-10 lg:px-14 lg:py-12">
          {/* Filet rouge éditorial */}
          <span className="absolute left-0 top-7 bottom-7 w-[3px] bg-[hsl(var(--primary))] hidden lg:block" />

          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardCheck className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                  Épreuve plateau moto · A1 / A2
                </span>
              </div>

              <h1
                className="font-extrabold tracking-tight text-foreground leading-[1.02] max-w-3xl"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 'clamp(2rem, 5.5vw, 3.75rem)',
                }}
              >
                Le parcours en{' '}
                <span className="text-[hsl(var(--primary))] italic font-medium">
                  6 manœuvres
                </span>
              </h1>

              <p
                className="text-sm sm:text-base text-foreground/65 mt-3 max-w-2xl leading-relaxed italic"
                style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
              >
                10 minutes pour démontrer ta maîtrise. Évaluation A / B / C par
                manœuvre, parcours chronométré, équipement obligatoire.
              </p>
            </div>

            {/* ScoreRing + acquis */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="text-right">
                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-foreground/55">
                  Acquis
                </div>
                <div
                  className="text-2xl sm:text-3xl font-black tabular-nums tracking-tight text-foreground"
                  data-testid="plateau-progress"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {doneSteps}
                  <span className="text-foreground/35 text-base">/{totalSteps}</span>
                </div>
              </div>
              <ScoreRing
                value={doneSteps}
                total={totalSteps}
                size={72}
                stroke={7}
                testid="plateau-score-ring"
              />
            </div>
          </div>

          {/* Barre progression globale */}
          <div className="relative h-1.5 bg-border/40 rounded-full mt-5 overflow-hidden">
            <div
              className="h-full c2r-progress-glow transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </section>

      {/* Bannière pédagogique — Cours plateau premium (4 sessions) */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 print:hidden" data-testid="plateau-pedago-cta">
        <Link
          to="/cours/premiere-plateau-a2"
          className="group relative overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-600/20 via-red-500/10 to-transparent p-4 sm:p-5 hover:border-red-500/60 hover:from-red-600/30 transition-all"
          data-testid="cta-cours-premiere-plateau"
        >
          <div className="pointer-events-none absolute -right-8 -bottom-6 opacity-10 group-hover:opacity-20 transition">
            <Presentation size={110} strokeWidth={1.2} className="text-red-500" />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">
              <Sparkles className="w-3 h-3" /> Session 1
            </div>
            <div className="text-base sm:text-lg font-black leading-tight text-foreground mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ma 1ʳᵉ séance plateau
            </div>
            <p className="text-xs sm:text-[13px] text-muted-foreground leading-snug mb-3 max-w-[85%]">
              Tu découvres, tu ressens, tu contrôles ta moto.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-red-500 group-hover:gap-2.5 transition-all">
              Ouvrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>

        <Link
          to="/cours/deuxieme-plateau-a2"
          className="group relative overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-600/20 via-red-500/10 to-transparent p-4 sm:p-5 hover:border-red-500/60 hover:from-red-600/30 transition-all"
          data-testid="cta-cours-deuxieme-plateau"
        >
          <div className="pointer-events-none absolute -right-8 -bottom-6 opacity-10 group-hover:opacity-20 transition">
            <Presentation size={110} strokeWidth={1.2} className="text-red-500" />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">
              <Sparkles className="w-3 h-3" /> Session 2
            </div>
            <div className="text-base sm:text-lg font-black leading-tight text-foreground mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ma 2ᵉ séance plateau
            </div>
            <p className="text-xs sm:text-[13px] text-muted-foreground leading-snug mb-3 max-w-[85%]">
              Tu stabilises, tu coordonnes, tu gagnes en précision.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-red-500 group-hover:gap-2.5 transition-all">
              Ouvrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>

        <Link
          to="/cours/troisieme-plateau-a2"
          className="group relative overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-br from-red-600/20 via-red-500/10 to-transparent p-4 sm:p-5 hover:border-red-500/60 hover:from-red-600/30 transition-all"
          data-testid="cta-cours-troisieme-plateau"
        >
          <div className="pointer-events-none absolute -right-8 -bottom-6 opacity-10 group-hover:opacity-20 transition">
            <Presentation size={110} strokeWidth={1.2} className="text-red-500" />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">
              <Sparkles className="w-3 h-3" /> Session 3
            </div>
            <div className="text-base sm:text-lg font-black leading-tight text-foreground mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ma 3ᵉ séance plateau
            </div>
            <p className="text-xs sm:text-[13px] text-muted-foreground leading-snug mb-3 max-w-[85%]">
              Tu accélères, tu freines, tu évites, tu enchaînes.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-red-500 group-hover:gap-2.5 transition-all">
              Ouvrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>

        <Link
          to="/cours/quatrieme-plateau-a2"
          className="group relative overflow-hidden rounded-2xl border-2 border-red-500/50 bg-gradient-to-br from-red-600/30 via-red-500/15 to-transparent p-4 sm:p-5 hover:border-red-500/80 hover:from-red-600/40 transition-all"
          data-testid="cta-cours-quatrieme-plateau"
        >
          <div className="pointer-events-none absolute -right-8 -bottom-6 opacity-10 group-hover:opacity-20 transition">
            <Trophy size={110} strokeWidth={1.2} className="text-red-500" />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">
              <Trophy className="w-3 h-3" /> Session 4 · Final
            </div>
            <div className="text-base sm:text-lg font-black leading-tight text-foreground mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ma 4ᵉ séance plateau
            </div>
            <p className="text-xs sm:text-[13px] text-muted-foreground leading-snug mb-3 max-w-[85%]">
              Tu consolides, tu t'auto-corriges, tu deviens autonome.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-red-500 group-hover:gap-2.5 transition-all">
              Ouvrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>
      </section>

      {/* ====================================================================
          3 CARDS CONTEXT — Objectifs / Équipement / Rappels
          ==================================================================== */}
      <section className="grid md:grid-cols-3 gap-4" data-testid="plateau-context">
        {/* Objectifs */}
        <div className="relative overflow-hidden rounded-xl bg-card border border-border/40 p-5">
          <span className="absolute top-0 inset-x-0 h-[3px] bg-[hsl(var(--primary))]" />
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center">
              <Target className="w-4 h-4" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[hsl(var(--primary))]">
              Objectifs · 5 maîtrises
            </span>
          </div>
          <ul className="space-y-2 text-sm">
            {OBJECTIFS.map((o, i) => (
              <li key={i} className="flex gap-2.5 leading-snug">
                <span className="text-[hsl(var(--primary))] flex-shrink-0 font-bold mt-0.5">→</span>
                <span className="text-foreground/85">{o}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Équipement */}
        <div className="relative overflow-hidden rounded-xl bg-card border border-border/40 p-5">
          <span className="absolute top-0 inset-x-0 h-[3px]" style={{ background: '#f59e0b' }} />
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: '#f59e0b1a', color: '#f59e0b' }}
            >
              <Shirt className="w-4 h-4" />
            </div>
            <span
              className="text-[10px] font-black uppercase tracking-[0.16em]"
              style={{ color: '#f59e0b' }}
            >
              Équipement obligatoire
            </span>
          </div>
          <ul className="space-y-1.5 text-sm">
            {EQUIPEMENT.map((e, i) => (
              <li key={i}>
                <label
                  className="inline-flex items-start gap-2.5 cursor-pointer w-full hover:bg-background/40 -mx-1.5 px-1.5 py-1 rounded transition-colors"
                  data-testid={`plateau-equipement-${i}`}
                >
                  <input
                    type="checkbox"
                    checked={!!state.equipement?.[i]}
                    onChange={() => toggleEquipement(i)}
                    className="mt-1 w-4 h-4 accent-[hsl(var(--primary))] flex-shrink-0"
                  />
                  <span
                    className={`leading-snug ${
                      state.equipement?.[i]
                        ? 'line-through text-foreground/40'
                        : 'text-foreground/85'
                    }`}
                  >
                    {e}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-3 px-3 py-2 rounded-lg border text-[11px] leading-snug flex items-start gap-1.5 bg-background/40"
            style={{ borderColor: 'hsl(var(--primary) / 0.35)' }}>
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 text-[hsl(var(--primary))] mt-0.5" />
            <span>
              <strong className="text-[hsl(var(--primary))]">Équipement non conforme :</strong>{' '}
              examen refusé ou noté à la baisse.
            </span>
          </div>
        </div>

        {/* Rappels */}
        <div className="relative overflow-hidden rounded-xl bg-card border border-border/40 p-5">
          <span className="absolute top-0 inset-x-0 h-[3px]" style={{ background: '#0891b2' }} />
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: '#0891b21a', color: '#0891b2' }}
            >
              <AlertOctagon className="w-4 h-4" />
            </div>
            <span
              className="text-[10px] font-black uppercase tracking-[0.16em]"
              style={{ color: '#0891b2' }}
            >
              Rappels · règles
            </span>
          </div>
          <ul className="space-y-2 text-sm">
            {RAPPELS.map((r, i) => {
              const Icon = r.icon;
              return (
                <li key={i} className="flex gap-2.5 leading-snug">
                  <Icon
                    className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                    style={{ color: '#0891b2' }}
                  />
                  <span className="text-foreground/85">{r.text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ====================================================================
          LES 6 MANŒUVRES — numéro XXL + accent coloré + checklist
          ==================================================================== */}
      <section data-testid="plateau-manoeuvres">
        <div className="flex items-end justify-between mb-4 sm:mb-5 gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                Checklist officielle · {doneSteps}/{totalSteps} étapes
              </span>
            </div>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Les 6 manœuvres
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {MANOEUVRES.map((m) => {
            const done = (state.manoeuvres?.[m.id] || []).length;
            const total = m.steps.length;
            const allDone = done === total && done > 0;
            const num = String(m.id).padStart(2, '0');
            return (
              <div
                key={m.id}
                className={`group relative overflow-hidden rounded-xl bg-card border transition-all duration-300 ${
                  allDone ? 'c2r-section-done' : ''
                }`}
                data-testid={`plateau-manoeuvre-${m.id}`}
                style={{
                  borderColor: allDone ? `${m.accent}88` : 'hsl(var(--border) / 0.4)',
                  boxShadow: '0 6px 18px -12px rgba(0,0,0,0.4)',
                }}
              >
                <CelebrateBurst trigger={burstTriggers[m.id] || 0} />
                {/* Filet d'accent coloré en haut */}
                <span
                  className="absolute top-0 inset-x-0 h-[3px] z-10"
                  style={{ background: m.accent }}
                />

                {/* Numéro XXL en background */}
                <span
                  aria-hidden="true"
                  className="absolute right-2 top-0 leading-none font-black opacity-[0.10] tracking-tighter pointer-events-none transition-opacity duration-500 group-hover:opacity-[0.16] z-0"
                  style={{
                    color: m.accent,
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: '6.5rem',
                  }}
                >
                  {num}
                </span>

                <div className="relative z-[1] p-5">
                  {/* Header manœuvre */}
                  <div className="flex items-center gap-2.5 mb-3 pr-12">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center font-black text-sm flex-shrink-0"
                      style={{
                        background: `${m.accent}1f`,
                        color: m.accent,
                      }}
                    >
                      {num}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span
                        className="text-[9px] font-black uppercase tracking-[0.18em] block leading-none"
                        style={{ color: m.accent }}
                      >
                        Manœuvre {m.id}
                      </span>
                      <h3
                        className="text-base sm:text-lg font-extrabold tracking-tight text-foreground leading-tight mt-0.5"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                      >
                        {m.title}
                      </h3>
                    </div>
                    {allDone && (
                      <span
                        className="ml-auto text-[10px] font-black px-2 py-0.5 rounded-full c2r-badge-pop inline-flex items-center gap-1 flex-shrink-0"
                        style={{
                          background: `${m.accent}1f`,
                          color: m.accent,
                        }}
                      >
                        <Sparkles className="w-3 h-3" />
                        OK
                      </span>
                    )}
                  </div>

                  {/* Steps */}
                  <ol className="space-y-2.5">
                    {m.steps.map((s, idx) => {
                      const checked = (state.manoeuvres?.[m.id] || []).includes(idx);
                      return (
                        <li key={idx}>
                          <label
                            className="flex items-start gap-2.5 cursor-pointer hover:bg-background/40 -mx-1.5 px-1.5 py-1 rounded transition-colors"
                            data-testid={`plateau-step-${m.id}-${idx}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleManoeuvre(m.id, idx)}
                              className="mt-1 w-4 h-4 accent-[hsl(var(--primary))] flex-shrink-0"
                            />
                            <span className="flex gap-2 text-sm leading-snug min-w-0">
                              <span
                                className="w-5 h-5 rounded-full grid place-items-center font-black text-[10px] flex-shrink-0"
                                style={{
                                  background: `${m.accent}1f`,
                                  color: m.accent,
                                }}
                              >
                                {idx + 1}
                              </span>
                              <span
                                className={
                                  checked
                                    ? 'line-through text-foreground/40'
                                    : 'text-foreground/85'
                                }
                              >
                                {s}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Roadbook officiel · 2026 — composant interactif (5 trajectoires animées) */}
      <PlateauInteractive />

      {/* ====================================================================
          ÉVALUATION A / B / C — modernisée avec gros badges
          ==================================================================== */}
      <section data-testid="plateau-eval">
        <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                Notation officielle
              </span>
            </div>
            <h2
              className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Évaluation A / B / C
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              grade: 'A',
              color: '#16a34a',
              title: 'Exercice correctement réalisé',
              detail:
                "Aucune erreur, manœuvre conforme aux exigences officielles. Tu valides l'exercice.",
            },
            {
              grade: 'B',
              color: '#f59e0b',
              title: 'Une erreur',
              detail:
                "Cône ou piquet déplacé, mauvaise maîtrise de l'embrayage, pieds au sol, 3ᵉ vitesse non enclenchée.",
            },
            {
              grade: 'C',
              color: '#cf2031',
              title: 'Deux erreurs ou élimination',
              detail:
                "Recalé sur cet exercice. Conséquence : recalage de l'épreuve plateau si répété.",
            },
          ].map((g) => (
            <div
              key={g.grade}
              className="relative overflow-hidden rounded-xl bg-card border border-border/40 p-5"
              style={{ boxShadow: '0 4px 16px -10px rgba(0,0,0,0.4)' }}
            >
              <span
                className="absolute top-0 inset-x-0 h-[3px]"
                style={{ background: g.color }}
              />
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="w-12 h-12 rounded-full grid place-items-center font-black text-white text-2xl shadow-lg"
                  style={{
                    background: g.color,
                    fontFamily: "'Playfair Display', Georgia, serif",
                  }}
                >
                  {g.grade}
                </span>
                <h3
                  className="text-base font-extrabold tracking-tight text-foreground leading-tight"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {g.title}
                </h3>
              </div>
              <p className="text-sm text-foreground/70 leading-relaxed">{g.detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ====================================================================
          ÉLIMINATIONS détaillées + nombre d'essais
          ==================================================================== */}
      <section
        className="grid md:grid-cols-2 gap-4"
        data-testid="plateau-essais"
      >
        <div className="relative overflow-hidden rounded-xl bg-card border border-border/40 p-5">
          <span className="absolute top-0 inset-x-0 h-[3px] bg-[hsl(var(--primary))]" />
          <div className="flex items-center gap-2 mb-3">
            <AlertOctagon className="w-4 h-4 text-[hsl(var(--primary))]" />
            <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[hsl(var(--primary))]">
              Les éliminations · Note C
            </span>
          </div>
          <p className="text-sm text-foreground/75 leading-relaxed">
            Embrayage non acquis, erreur de parcours, obstacle ou porte non franchi,
            vitesse minimale non atteinte, distance d'arrêt trop longue, anticipation
            du freinage d'urgence, exercice non réalisé, chute de la moto, renversement
            ou déplacement de cône lors de l'évitement, sortie de terrain.
          </p>
        </div>

        <div className="relative overflow-hidden rounded-xl bg-card border border-border/40 p-5">
          <span
            className="absolute top-0 inset-x-0 h-[3px]"
            style={{ background: '#0891b2' }}
          />
          <div className="flex items-center gap-2 mb-3">
            <Timer className="w-4 h-4" style={{ color: '#0891b2' }} />
            <span
              className="text-[10px] font-black uppercase tracking-[0.16em]"
              style={{ color: '#0891b2' }}
            >
              Nombre d'essais
            </span>
          </div>
          <ul className="space-y-2 text-sm text-foreground/85 leading-relaxed">
            <li className="flex gap-2">
              <span className="text-[hsl(var(--primary))] flex-shrink-0 mt-0.5">•</span>
              <span>
                <strong className="text-foreground">1 essai</strong> pour la manœuvre 1.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-[hsl(var(--primary))] flex-shrink-0 mt-0.5">•</span>
              <span>
                <strong className="text-foreground">2 essais</strong> pour l'enchaînement
                des manœuvres 2 à 6.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-[hsl(var(--primary))] flex-shrink-0 mt-0.5">•</span>
              <span>
                Pieds au sol : <strong className="text-foreground">3 fois autorisés</strong>{' '}
                à partir de la manœuvre 2 (hors zones neutralisées).
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-[hsl(var(--primary))] flex-shrink-0 mt-0.5">•</span>
              <span>
                Si erreur sur manœuvre 1 : pieds autorisés{' '}
                <strong className="text-foreground">réduits de 3 à 2</strong>.
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* ====================================================================
          ACTIONS bas de page (print/reset)
          ==================================================================== */}
      <div
        className="flex flex-wrap gap-2 print:hidden"
        data-testid="plateau-actions"
      >
        <Button
          variant="outline"
          onClick={() => window.print()}
          className="rounded-full"
          size="sm"
        >
          <Printer className="w-4 h-4 mr-1.5" /> Imprimer
        </Button>
        <Button
          variant="outline"
          onClick={reset}
          className="rounded-full"
          size="sm"
          data-testid="plateau-reset"
        >
          <RotateCcw className="w-4 h-4 mr-1.5" /> Réinitialiser
        </Button>
      </div>

      <p
        className="text-[11px] text-foreground/45 italic leading-relaxed mt-2"
        style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
      >
        Sécurité — Maîtrise — Précision : les clés de la réussite. Source :
        programme officiel ETM (arrêté du 23 avril 2012, REMC).
      </p>

      {/* MAILLAGE INTERNE — boost SEO + temps de session */}
      <div className="print:hidden">
        <RelatedLinks topic="plateau" max={5} />
      </div>
    </div>
  );
}
